import { extractErrorCode, messageForErrorCode } from '@/api/apiError';

/**
 * Traduction des erreurs propres a l'administration.
 *
 * Pourquoi une seconde table alors que `src/api/apiError.ts` en porte deja une :
 *
 * 1. **Deux codes n'y figurent pas.** `confirmation_mismatch` est rendu par les
 *    seules routes d'administration ; affiche par le repli generique, il donne
 *    « Une erreur est survenue (code « confirmation_mismatch ») », c'est-a-dire
 *    le nom anglais du defaut a l'endroit precis ou l'utilisateur doit
 *    comprendre qu'il a mal recopie un nom.
 * 2. **`forbidden` y veut dire autre chose.** Le message commun parle des droits
 *    « sur ce profil bebe » — vrai des neuf ressources metier, faux ici, ou le
 *    refus porte sur le role d'exploitation et n'a aucun rapport avec un bebe.
 *
 * Le repli reste celui du projet : un code inconnu passe par
 * `messageForErrorCode`, qui le nomme au lieu de l'avaler. Cette table ne
 * masque donc jamais un code, elle en precise deux.
 *
 * Elle vit sous `admin/settings/` parce que c'est le perimetre de ce lot. Si un
 * autre ecran d'administration en a besoin, elle est a remonter d'un dossier
 * — pas a recopier.
 */
const ADMIN_MESSAGES: Readonly<Record<string, string>> = {
  forbidden:
    "Votre rôle d’administration ne permet pas cette action. Le serveur l’a refusée : rien n’a été modifié.",
  confirmation_mismatch:
    'La confirmation ne reprend pas exactement le nom du réglage. Rien n’a été modifié.',
  not_found:
    'Ce réglage n’existe pas sur le serveur. Rechargez l’écran : la liste des réglages est déclarée par le serveur, pas par ce back-office.',
  validation_failed:
    'Le serveur a refusé la valeur ou le motif. Vérifiez le type attendu et la longueur du motif (500 caractères au plus).',
};

/** Statut HTTP porte par une `HttpError` de React Admin, quand il y en a un. */
const statusOf = (error: unknown): number | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  const status: unknown = (error as { status?: unknown }).status;

  return typeof status === 'number' ? status : null;
};

/** Code d'erreur de l'enveloppe `{error: {code}}`, quand il y en a un. */
export const adminErrorCode = (error: unknown): string | null => {
  if (typeof error !== 'object' || error === null) {
    return null;
  }

  return extractErrorCode((error as { body?: unknown }).body);
};

/**
 * Le serveur a-t-il refuse l'acces faute de role ?
 *
 * Le statut suffit et le code le confirme : `AdminAccessGuard` rend `403
 * forbidden` a un compte parent ordinaire — il ne rend **jamais** un 200 portant
 * `adminRole: null`. L'ecran doit donc reconnaitre le 403 pour expliquer au lieu
 * d'afficher une panne.
 *
 * Vaut pour la **lecture initiale seulement**. Un 403 sur une ecriture n'est pas
 * un compte sans role : c'est un compte `support` qui a atteint un bouton qu'il
 * n'aurait pas du voir, et l'ecran doit alors rester affiche.
 */
export const isAccessDenied = (error: unknown): boolean =>
  statusOf(error) === 403 || adminErrorCode(error) === 'forbidden';

/** Message francais d'une erreur d'administration, jamais un objet brut. */
export const describeAdminError = (error: unknown): string => {
  const code = adminErrorCode(error);

  if (code !== null) {
    return ADMIN_MESSAGES[code] ?? messageForErrorCode(code);
  }

  if (error instanceof Error && error.message !== '') {
    return error.message;
  }

  return 'Une erreur est survenue. Réessayez dans un instant.';
};
